/* eslint-disable @typescript-eslint/no-explicit-any */

import { useTranslation } from '@/i18n/client';
import { useStores } from '@/models';
import { observer } from 'mobx-react-lite';
import { FC, useState } from 'react';

import { redirectPayResult, repayOrderJD } from '@/apis/checkout/checkout.api';
import {
  PAY_RESULT_CODE,
  PAY_RESULT_SPECIAL_ORDER_TYPE_ENUM,
  PAY_STATUS,
  PAY_TYPE,
} from '@/apis/checkout/constants/constants';

import ApplePayButton from './payButton/apple';
import CardButton from './payButton/card';
import CommonPayBtn from './payButton/common';
import Default from './payButton/default';
import GooglePayButton from './payButton/google';
import KlarnaPay from './payButton/klarna';
import PaypalButton from './payButton/paypal';
import WechatPayButton from './payButton/wechat';

import { sgmBizTrackSubmit } from '@/apis/checkout/utils/sgmTrack';
import { EXPOSURE_RECORD, sendClickTrack } from '@/tracks/26172';
import { encryption } from '@/utils/cryptico';
import {
  SGM_PAY_METHOD,
  SGM_PAYMENT_TIME,
  SGM_TRACK_MAIN_TYPE,
  SGM_TRACK_TYPE,
} from '@/utils/logger/instance/checkoutBusinessLogger';
import PayCommonErrorModal from './payCommonErrorModal';
import PayRiskModal from './payRiskModal';
import Styles from './repayButtons.module.scss';
import { sendClickTrack as member_sendClickTrack } from '@/tracks/32416';
interface RepayButtonProps {
  onSubmitError: (data: any) => void; // 提单失败后的回调
  onFinish: () => void; // 支付完成后的回调
  onFresh: () => void; // 刷新订单状态
  onClose: () => void; // 关闭弹窗
  // 是否是购买会员支付 PAY_RESULT_SPECIAL_ORDER_TYPE_ENUM.PURCHASE_MEMBER
  specialOrderType?: PAY_RESULT_SPECIAL_ORDER_TYPE_ENUM;
}

const REPAY_RESULT_CODE = {
  SUCCESS: '200',
};
// 提单失败，给支付的默认兜底数据
const DEFAULT_RESULT_DATA = {
  success: false,
  data: null,
};

const CANCEL_URL = '/profile/my-orders';

const RepayButton: FC<RepayButtonProps> = observer((props) => {
  const { checkoutStore } = useStores();
  const { t } = useTranslation();
  const { paymentStore } = checkoutStore;
  const { setPayResult } = paymentStore;

  const [riskErrorOpen, setRiskErrorOpen] = useState(false);
  const [commonError, setCommonError] = useState('');
  //支付失败弹窗
  const [commonPayErrorOpen, setCommonPayErrorOpen] = useState(false);
  const { specialOrderType } = props;

  /**
   *
   * 二次支付逻辑处理
   * 失败统一处理；成功使用各支付方式的回调方法
   *
   */
  const handleSubmitOrder = async () => {
    sendClickTrack.Cashier_Pop_Pay({
      orderId: checkoutStore.repayOrderId || '-100',
      payWay: String(paymentStore.payMethod),
      channelName: (paymentStore.currentPayInfo as any)?.payEnum || '-100',
    });

    const submitParams = getSubmitOrderParams();
    // @ts-ignore
    const res = await repayOrderJD(submitParams);
    console.log('handleSubmitOrder', res);

    setPayResult({
      // 支付流水号
      id: (res as any)?.data?.txnSeqno || '',
      // 订单ID
      orderId: checkoutStore.repayOrderId || '',
    });

    switch (res.code) {
      case REPAY_RESULT_CODE.SUCCESS:
        return {
          success: true,
          data: res.data,
        };
      // 科技风控
      case PAY_RESULT_CODE.PAY_RISK_MANAGEMENT:
        setRiskErrorOpen(true);
        setCommonError(res.msg);
        // SGM错误日志上报
        sgmBizTrackSubmit(
          {
            type: SGM_TRACK_MAIN_TYPE.CHECKOUT_API_ERROR,
            detailCode: SGM_TRACK_TYPE.REPAY_ORDER_PAY_RISK_MANAGEMENT_ERROR,
            paymentTime: SGM_PAYMENT_TIME.REPAY,
            payMethod: SGM_PAY_METHOD[paymentStore.payMethod],
          },
          {
            message: {
              traceId: res?.traceId,
              code: res?.code,
              orderId: checkoutStore.repayOrderId || '',
            },
          },
        );

        return {
          ...DEFAULT_RESULT_DATA,
        };
      default:
        handlePayError(res.msg);

        // SGM错误日志上报
        sgmBizTrackSubmit(
          {
            type: SGM_TRACK_MAIN_TYPE.CHECKOUT_API_ERROR,
            detailCode: SGM_TRACK_TYPE.REPAY_ORDER_COMMON_ERROR,
            paymentTime: SGM_PAYMENT_TIME.REPAY,
            payMethod: SGM_PAY_METHOD[paymentStore.payMethod],
          },
          {
            message: {
              traceId: res?.traceId,
              code: res?.code,
              orderId: checkoutStore.repayOrderId || '',
            },
          },
        );

        return {
          ...DEFAULT_RESULT_DATA,
        };
    }
  };

  // 提单参数
  const getSubmitOrderParams = () => {
    const submitParams: Record<string, any> = {
      orderId: checkoutStore.repayOrderId || '',
      payMethod: paymentStore.payMethod,
      extParam: {
        cancelUrl: CANCEL_URL,
        payEnum: (paymentStore.currentPayInfo as any).payEnum,
        integrationType: (paymentStore.currentPayInfo as any).integrationType,
        browserInfo: paymentStore.isBnpCreditCardForm
          ? JSON.stringify(checkoutStore.browserInfo)
          : undefined,
      },
      sourceType: '4',
      rememberPayAccount:
        (paymentStore as any)?.currentPayInfo.rememberInfo?.rememberCheckbox || false, // 是否记住支付账号
      payAccountSeqNo: (paymentStore as any).currentPayAccount?.payAccountSeqNo || '', // 支付账号id
    };

    if (paymentStore.payMethod === PAY_TYPE.JD_CREDIT_CARD && !paymentStore.isQuickPay) {
      const creditCardForm = checkoutStore.creditCardStore.jdCreditCardForm;
      const cardNum = creditCardForm.cardNo && creditCardForm.cardNo.replace(/\s+/g, '');
      submitParams['cardInfo'] = JSON.stringify({
        // 卡号，明文
        cardNum: encryption(checkoutStore.creditCardStore.encryption.publicKey, cardNum),

        //有效期月
        expiryMonth: encryption(
          checkoutStore.creditCardStore.encryption.publicKey,
          creditCardForm.expireDateMonth,
        ),

        // 有效期年
        expiryYear: encryption(
          checkoutStore.creditCardStore.encryption.publicKey,
          creditCardForm.expireDateYear,
        ),

        // 新卡时必传，已绑卡可不传，系统不做校验
        cvv: encryption(checkoutStore.creditCardStore.encryption.publicKey, creditCardForm.cvv),

        // 卡号匹配的卡bin信息
        cardBinInfos: (checkoutStore.creditCardStore?.cardBinInfos || [])
          .filter(({ cardOrgCode }: any) => cardOrgCode)
          .map((item: any) => {
            const cardBinInfo: Record<string, string> = {};
            if (item.cardOrgCode) cardBinInfo.cardOrgCode = item.cardOrgCode;
            if (item.cardType) cardBinInfo.cardType = item.cardType;
            if (item.issuingCountryCode) cardBinInfo.issuingCountryCode = item.issuingCountryCode;
            if (item.issuerName) cardBinInfo.issuerName = item.issuerName;
            return cardBinInfo;
          }),
      });
    }

    return submitParams;
  };

  // 支付报错，弹出报错信息，弹出支付报错弹窗
  const handlePayError = (error: string) => {
    setCommonError(error);
    setCommonPayErrorOpen(true);
  };

  // 支付完成之后，轮询支付结果
  const redirectResult = async (
    requestData: any,
    closeLoading: () => void,
    handlePayCb?: (type: string) => void,
  ) => {
    let retryAttempts = 12; // 总尝试次数（包含首次请求)

    while (retryAttempts > 0) {
      const result: any = await redirectPayResult(requestData);
      const payRes = result.data;
      console.log('payRes', payRes);

      if (payRes?.authUrl) {
        closeLoading();
        window.location.replace(payRes.authUrl);
        return;
      }

      switch (payRes?.status) {
        case PAY_STATUS.ERROR:
          if (handlePayCb) {
            handlePayCb((window as any).ApplePaySession.STATUS_FAILURE);
          }
          closeLoading();
          handlePayError(result.msg);
          // SGM错误日志上报
          sgmBizTrackSubmit(
            {
              type: SGM_TRACK_MAIN_TYPE.CHECKOUT_API_ERROR,
              detailCode: SGM_TRACK_TYPE.REPAY_ORDER_REDIRECT_FAILED,
              paymentTime: SGM_PAYMENT_TIME.REPAY,
              payMethod: SGM_PAY_METHOD[paymentStore.payMethod],
              retryAttempts: 12 - retryAttempts + 1, // 重试次数
            },
            {
              message: {
                traceId: result?.traceId,
                code: result?.code,
                orderId: payRes?.orderId,
              },
            },
          );

          return;
        case PAY_STATUS.SUCCESS:
          if (handlePayCb) {
            handlePayCb((window as any).ApplePaySession.STATUS_SUCCESS);
          }
          closeLoading();
          props.onFinish();
          return;
        default:
          // 最后一次尝试后仍未成功，直接跳转支付结果页面
          if (retryAttempts === 1) {
            if (handlePayCb) {
              handlePayCb((window as any).ApplePaySession.STATUS_FAILURE);
            }
            closeLoading();
            handlePayError(result.msg);
            // SGM错误日志上报
            sgmBizTrackSubmit(
              {
                type: SGM_TRACK_MAIN_TYPE.CHECKOUT_API_ERROR,
                detailCode: SGM_TRACK_TYPE.REPAY_ORDER_REDIRECT_FINISH,
                paymentTime: SGM_PAYMENT_TIME.REPAY,
                payMethod: SGM_PAY_METHOD[paymentStore.payMethod],
                retryAttempts: 12 - retryAttempts + 1,
              },
              {
                message: {
                  traceId: result?.traceId,
                  code: result?.code,
                  orderId: payRes?.orderId,
                },
              },
            );

            return;
          }
          retryAttempts--;
          await new Promise((resolve) => setTimeout(resolve, 5000)); // 等待 5 秒后重试
      }
    }
  };

  return (
    <div
      className={Styles.repayButtonBox}
      exptag={'exp|' + EXPOSURE_RECORD.Cashier_Pop_PayExpo}
      data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
      data-exptag-json-param={JSON.stringify({
        orderId: checkoutStore.repayOrderId || '-100',
        payWay: paymentStore.payMethod ? String(paymentStore.payMethod) : '-100',
        channelName: (paymentStore.currentPayInfo as any)?.payEnum || '-100',
      })}
      onClick={() => {
        // 点击支付按钮埋点
        member_sendClickTrack.place_order_Click();
      }}
    >
      {!paymentStore.payMethod && <Default />}
      {paymentStore.payMethod === PAY_TYPE.CREDIT_CARD ||
      paymentStore.payMethod === PAY_TYPE.JD_CREDIT_CARD ? (
        <CardButton
          onSubmit={handleSubmitOrder}
          isFirstPayment={false}
          onLoadingResult={redirectResult}
          specialOrderType={props.specialOrderType}
        />
      ) : (
        ''
      )}
      <div
        style={{
          display: paymentStore.payMethod === PAY_TYPE.PAYPAL ? 'block' : 'none',
          width: '100%',
        }}
      >
        <PaypalButton
          onSubmit={handleSubmitOrder}
          onLoadingResult={redirectResult}
          isFirstPayment={false}
        />
      </div>
      {/* google pay */}

      {paymentStore.payMethod === PAY_TYPE.GOOGLE_PAY && (
        <GooglePayButton
          onSubmit={handleSubmitOrder}
          onLoadingResult={redirectResult}
          isFirstPayment={false}
        />
      )}
      {/* 分期 */}
      {paymentStore.payMethod === PAY_TYPE.KLARNA && (
        <KlarnaPay
          onSubmit={handleSubmitOrder}
          onLoadingResult={redirectResult}
          isFirstPayment={false}
        />
      )}

      {/* wechat pay */}
      {paymentStore.payMethod === PAY_TYPE.WECHAT_PAY && (
        <WechatPayButton
          onSubmit={handleSubmitOrder}
          onLoadingResult={redirectResult}
          isFirstPayment={false}
        />
      )}
      {/* Apple pay */}
      {paymentStore.payMethod === PAY_TYPE.APPLE && (
        <ApplePayButton
          onSubmit={handleSubmitOrder}
          onLoadingResult={redirectResult}
          isFirstPayment={false}
        />
      )}

      {/* 本地支付 IDEAl Bancontact */}
      {(paymentStore.payMethod === PAY_TYPE.IDEAL ||
        paymentStore.payMethod === PAY_TYPE.BANCONTACT) && (
        <CommonPayBtn onSubmit={handleSubmitOrder} isFirstPayment={false} />
      )}

      {/* 支付风控 */}

      {riskErrorOpen && (
        <PayRiskModal
          open={riskErrorOpen}
          onClose={() => {
            props.onClose();
            setRiskErrorOpen(false);
          }}
          onOk={
            specialOrderType === PAY_RESULT_SPECIAL_ORDER_TYPE_ENUM.PURCHASE_MEMBER
              ? null
              : () => {
                  props.onFresh();
                  setRiskErrorOpen(false);
                }
          }
          content={commonError}
        />
      )}

      {/* 提单成功，支付失败，弹窗提示 */}
      {commonPayErrorOpen && (
        <PayCommonErrorModal
          open={commonPayErrorOpen}
          content={commonError}
          handleOk={() => {
            setCommonError('');
            setCommonPayErrorOpen(false);
          }}
        ></PayCommonErrorModal>
      )}
    </div>
  );
});

export default RepayButton;
